import React, { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { CardPicker } from "@/components/CardPicker";
import { Tag } from "@/components/Badges";
import { VerifyBanner } from "@/components/VerifyBanner";
import { Loading, EmptyState } from "@/components/States";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Heart, Link2, Info } from "lucide-react";
import { toast } from "sonner";

const CONDS = ["Mint", "Near Mint", "Excellent", "Light Played", "Played", "Poor"];
const LANGS = ["IT", "EN", "JP", "FR", "DE", "ES"];
const PRIOS = [["bassa", "Bassa"], ["media", "Media"], ["alta", "Alta"]];
const GRADES = [["indifferente", "Indifferente"], ["non_gradata", "Non gradata"], ["gradata", "Gradata"]];
const PRIO_TONE = { bassa: "#94A3B8", media: "#3B82F6", alta: "#EF4444" };

const empty = { card: null, desired_qty: 1, languages: [], min_condition: "any", priority: "media", grading_pref: "indifferente", notes: "" };

export default function Wishlist() {
  const { user, refresh } = useAuth();
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [pub, setPub] = useState(user?.wishlist_public || false);

  const load = () => api.get("/wishlist/mine").then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);
  useEffect(() => { setPub(user?.wishlist_public || false); }, [user]);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (w) => { setEditing(w); setForm({ card: w.card, desired_qty: w.desired_qty, languages: w.languages || [], min_condition: w.min_condition || "any", priority: w.priority, grading_pref: w.grading_pref, notes: w.notes || "" }); setOpen(true); };

  const toggleLang = (l) => setForm((f) => ({ ...f, languages: f.languages.includes(l) ? f.languages.filter((x) => x !== l) : [...f.languages, l] }));

  const save = async () => {
    if (!form.card) { toast.error("Seleziona una carta."); return; }
    setSaving(true);
    const payload = { card_id: form.card.id, desired_qty: Number(form.desired_qty), languages: form.languages,
      min_condition: form.min_condition === "any" ? null : form.min_condition, priority: form.priority, grading_pref: form.grading_pref, notes: form.notes };
    try {
      if (editing) await api.put(`/wishlist/${editing.id}`, payload); else await api.post("/wishlist", payload);
      toast.success("Wishlist aggiornata."); setOpen(false); load();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  const remove = async (id) => { try { await api.delete(`/wishlist/${id}`); load(); } catch (e) { toast.error(apiError(e)); } };

  const togglePublic = async (v) => {
    setPub(v);
    try { await api.put("/settings/profile", { wishlist_public: v }); await refresh(); toast.success(v ? "Wishlist resa pubblica." : "Wishlist resa privata."); }
    catch (e) { toast.error(apiError(e)); setPub(!v); }
  };

  const copyLink = () => { navigator.clipboard.writeText(`${window.location.origin}/collezionista/${user.username}`); toast.success("Link del profilo copiato."); };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div><h1 className="font-display text-3xl font-bold text-[#F8FAFC]">Wishlist</h1><p className="mt-1 text-[#CBD5E1]">Le carte che stai cercando.</p></div>
        <Button onClick={openNew} disabled={!user?.email_verified} data-testid="add-wishlist-button" className="bg-[#7C3AED] hover:bg-[#6D28D9]"><Plus className="mr-2 h-4 w-4" />Aggiungi alla wishlist</Button>
      </div>
      <div className="mt-6"><VerifyBanner /></div>

      <div className="mb-6 rounded-xl border border-[#2A3655] bg-[#1C2640] p-4">
        <label className="flex items-center justify-between gap-3 text-sm text-[#CBD5E1]">
          <span>Rendi pubblica la wishlist<br /><span className="text-xs text-[#94A3B8]">Attivando il matching, le singole corrispondenze rilevanti possono essere mostrate ai collezionisti compatibili senza rendere visibile l'intera lista. La pubblicazione è volontaria.</span></span>
          <Switch checked={pub} onCheckedChange={togglePublic} data-testid="wishlist-public-toggle" />
        </label>
        {pub && <button onClick={copyLink} data-testid="wishlist-copy-link" className="mt-3 flex items-center gap-1 text-sm text-[#2DD4BF] hover:underline"><Link2 className="h-4 w-4" />Copia il link del profilo pubblico</button>}
      </div>

      {items === null ? <Loading /> : items.length === 0 ? (
        <EmptyState icon={Heart} title="Wishlist vuota" description="Aggiungi le carte che cerchi con priorità e preferenze: serviranno a trovare corrispondenze reciproche."
          action={<Button onClick={openNew} disabled={!user?.email_verified} className="bg-[#7C3AED] hover:bg-[#6D28D9]"><Plus className="mr-2 h-4 w-4" />Aggiungi alla wishlist</Button>} />
      ) : (
        <div className="space-y-2" data-testid="wishlist-list">
          {items.map((w) => (
            <div key={w.id} className="flex items-center justify-between rounded-xl border border-[#2A3655] bg-[#141B2D] p-3" data-testid="wishlist-item">
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold text-[#F8FAFC]">{w.card?.name} <span className="text-xs font-normal text-[#94A3B8]">· {w.card?.expansion_name || "—"}</span></p>
                <div className="mt-1 flex flex-wrap gap-1">
                  <Tag tone={PRIO_TONE[w.priority]}>Priorità {w.priority}</Tag>
                  <Tag tone="#94A3B8">×{w.desired_qty}</Tag>
                  <Tag>{(w.languages || []).length ? w.languages.join("/") : "Qualsiasi lingua"}</Tag>
                  <Tag>{w.min_condition ? `Min ${w.min_condition}` : "Qualsiasi condizione"}</Tag>
                  {w.grading_pref !== "indifferente" && <Tag tone="#7C3AED">{w.grading_pref === "gradata" ? "Gradata" : "Non gradata"}</Tag>}
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-1">
                <button onClick={() => openEdit(w)} data-testid="edit-wishlist" className="rounded p-1.5 text-[#CBD5E1] hover:bg-[#1C2640]"><Pencil className="h-4 w-4" /></button>
                <button onClick={() => remove(w.id)} data-testid="delete-wishlist" className="rounded p-1.5 text-[#CBD5E1] hover:bg-[#EF4444]/20 hover:text-[#EF4444]"><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto bg-[#141B2D] border-[#2A3655] text-[#F8FAFC] sm:max-w-lg">
          <DialogHeader><DialogTitle className="font-display">{editing ? "Modifica voce wishlist" : "Aggiungi alla wishlist"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            {!editing && <CardPicker onSelect={(c) => setForm({ ...form, card: c })} selectedId={form.card?.id} />}
            {form.card && (
              <>
                <div className="rounded-lg border border-[#2DD4BF]/30 bg-[#2DD4BF]/10 p-2 text-sm text-[#F8FAFC]">Carta cercata: <b>{form.card.name}</b></div>
                <div className="grid grid-cols-2 gap-2">
                  <div><Label className="text-xs text-[#CBD5E1]">Quantità desiderata</Label><Input type="number" min="1" data-testid="wish-qty" value={form.desired_qty} onChange={(e) => setForm({ ...form, desired_qty: e.target.value })} className="bg-[#0B1020] border-[#2A3655]" /></div>
                  <div><Label className="text-xs text-[#CBD5E1]">Priorità</Label>
                    <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}><SelectTrigger data-testid="wish-priority" className="bg-[#0B1020] border-[#2A3655]"><SelectValue /></SelectTrigger><SelectContent className="bg-[#141B2D] border-[#2A3655] text-[#F8FAFC]">{PRIOS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent></Select>
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-[#CBD5E1]">Lingue accettate (nessuna = qualsiasi)</Label>
                  <div className="mt-1 flex flex-wrap gap-1.5">
                    {LANGS.map((l) => <button key={l} type="button" onClick={() => toggleLang(l)} className={`rounded-md border px-2 py-1 text-xs ${form.languages.includes(l) ? "border-[#2DD4BF] bg-[#2DD4BF]/15 text-[#2DD4BF]" : "border-[#2A3655] text-[#CBD5E1]"}`}>{l}</button>)}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div><Label className="text-xs text-[#CBD5E1]">Condizione minima</Label>
                    <Select value={form.min_condition} onValueChange={(v) => setForm({ ...form, min_condition: v })}><SelectTrigger data-testid="wish-min-cond" className="bg-[#0B1020] border-[#2A3655]"><SelectValue /></SelectTrigger><SelectContent className="bg-[#141B2D] border-[#2A3655] text-[#F8FAFC]"><SelectItem value="any">Qualsiasi</SelectItem>{CONDS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select>
                  </div>
                  <div><Label className="text-xs text-[#CBD5E1]">Gradazione</Label>
                    <Select value={form.grading_pref} onValueChange={(v) => setForm({ ...form, grading_pref: v })}><SelectTrigger data-testid="wish-grading" className="bg-[#0B1020] border-[#2A3655]"><SelectValue /></SelectTrigger><SelectContent className="bg-[#141B2D] border-[#2A3655] text-[#F8FAFC]">{GRADES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent></Select>
                  </div>
                </div>
                <p className="flex items-start gap-1 text-xs text-[#64748B]"><Info className="mt-0.5 h-3 w-3 shrink-0" />Le preferenze obbligatorie (lingua, condizione minima, gradazione) escludono le carte non compatibili dal matching.</p>
                <div><Label className="text-xs text-[#CBD5E1]">Note</Label><Textarea data-testid="wish-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="bg-[#0B1020] border-[#2A3655]" /></div>
              </>
            )}
          </div>
          <DialogFooter><Button onClick={save} disabled={saving || !form.card} data-testid="save-wishlist" className="bg-[#7C3AED] hover:bg-[#6D28D9]">{saving ? "Salvataggio…" : "Salva"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
